import { createContext, useContext, useState } from "react";

import { useLocalStorage } from "usehooks-ts";

import { useCurrentPage } from "src/router/useCurrentPage";
import { useUnifiedCopilotChatSession } from "src/unifiedCopilot/chat/useUnifiedCopilotChatSession";

const UNIFIED_COPILOT_DISPLAY_MODE_STORAGE_KEY_PREFIX =
  "unifiedCopilotDisplayModeV1";

export type UnifiedCopilotCompanionMode = "floating" | "sidebar";
export type UnifiedCopilotSurface = "hidden" | "floating" | "sidebar" | "page";

type UnifiedCopilotDisplayContextValue = {
  surface: UnifiedCopilotSurface;
  preferredCompanionMode: UnifiedCopilotCompanionMode;
  open: () => void;
  hide: () => void;
  switchMode: (mode: UnifiedCopilotCompanionMode) => void;
};

type DisplayModeStorageKeyParams = {
  userId: string;
  workspaceId: string;
};

type UnifiedCopilotDisplayProviderProps = {
  children: React.ReactNode;
} & DisplayModeStorageKeyParams;

export const getUnifiedCopilotDisplayModeStorageKey = ({
  userId,
  workspaceId,
}: DisplayModeStorageKeyParams): string =>
  `${UNIFIED_COPILOT_DISPLAY_MODE_STORAGE_KEY_PREFIX}:${userId}:${workspaceId}`;

const UnifiedCopilotDisplayContext = createContext<
  UnifiedCopilotDisplayContextValue | undefined
>(undefined);

export const UnifiedCopilotDisplayProvider: React.FC<
  UnifiedCopilotDisplayProviderProps
> = ({ children, userId, workspaceId }) => {
  const { isCopilotPage } = useCurrentPage();
  const { messages, sessionState, queuedDrafts } =
    useUnifiedCopilotChatSession();
  const [preferredCompanionMode, setPreferredCompanionMode] =
    useLocalStorage<UnifiedCopilotCompanionMode>(
      getUnifiedCopilotDisplayModeStorageKey({ userId, workspaceId }),
      "floating",
    );
  const [isCompanionOpen, setIsCompanionOpen] = useState(false);
  const [previousIsCopilotPage, setPreviousIsCopilotPage] =
    useState(isCopilotPage);

  if (isCopilotPage !== previousIsCopilotPage) {
    setPreviousIsCopilotPage(isCopilotPage);
    const hasTransferableConversation =
      sessionState !== "idle" || messages.length > 0 || queuedDrafts.length > 0;
    setIsCompanionOpen(!isCopilotPage && hasTransferableConversation);
  }

  const surface: UnifiedCopilotSurface = isCopilotPage
    ? "page"
    : isCompanionOpen
      ? preferredCompanionMode
      : "hidden";

  const open = (): void => {
    if (isCopilotPage) return;
    setIsCompanionOpen(true);
  };

  const hide = (): void => {
    setIsCompanionOpen(false);
  };

  const switchMode = (mode: UnifiedCopilotCompanionMode): void => {
    if (isCopilotPage) return;
    setPreferredCompanionMode(mode);
    setIsCompanionOpen(true);
  };

  return (
    <UnifiedCopilotDisplayContext
      value={{
        surface,
        preferredCompanionMode,
        open,
        hide,
        switchMode,
      }}
    >
      {children}
    </UnifiedCopilotDisplayContext>
  );
};

export const useUnifiedCopilotDisplay =
  (): UnifiedCopilotDisplayContextValue => {
    const context = useContext(UnifiedCopilotDisplayContext);
    if (!context) {
      throw new Error(
        "useUnifiedCopilotDisplay must be used within a UnifiedCopilotDisplayProvider",
      );
    }
    return context;
  };
